import React, { useState, useEffect, useMemo } from "react";
import axiosInstance from "../../utils/axios/axiosConfig";
import { toast } from "react-toastify";
import { DeleteModal } from "../../components";
import CategoryModal from "./CategoryModal";
import ImportModal from "./ImportModal";
import CategoryTree, { CategoryRow, MAX_DEPTH } from "./CategoryTree";
import { useAccessChecker } from "../../utils/Functions/Authorization";
import { MagnifyingGlass, DownloadSimple, Plus, Tag, DotsSixVertical, ArrowsOutCardinal } from "@phosphor-icons/react";
import { PageHeader, Button, Input, EmptyState, Skeleton } from "../../components/ui";

/* flatten the tree with ancestor path, for client-side search */
const flattenWithPath = (nodes, path = [], acc = []) => {
  (nodes || []).forEach((n) => {
    acc.push({ node: n, path });
    if (n.children?.length) flattenWithPath(n.children, [...path, n.name], acc);
  });
  return acc;
};

export default function Categories() {
  const { checkAccess } = useAccessChecker();
  const canManage = {
    create: checkAccess("categories", "create"),
    update: checkAccess("categories", "update"),
    delete: checkAccess("categories", "delete"),
  };

  const [categoryModal, setCategoryModal] = useState(false);
  const [importModal, setImportModal] = useState(false);
  const [categoryToUpdate, setCategoryToUpdate] = useState(null);
  const [presetParentId, setPresetParentId] = useState(null);

  const [categories, setCategories] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done]);

  async function fetchCategories() {
    try {
      setLoading(true);
      const res = await axiosInstance.post(`/categories/search`, { searchValue: null });
      setCategories(res.data?.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || "Failed to fetch categories");
    } finally {
      setLoading(false);
    }
  }

  const persistOrder = async (payload) => {
    try {
      await axiosInstance.post("/categories/reorder", { items: payload });
    } catch (err) {
      toast.error(err?.response?.data?.message || "Couldn't save the new order");
      fetchCategories(); // revert to server truth
    }
  };

  const openCreate = (parentId = null) => {
    setCategoryToUpdate(null);
    setPresetParentId(parentId);
    setCategoryModal(true);
  };
  const openEdit = (category) => {
    setCategoryToUpdate(category);
    setPresetParentId(null);
    setCategoryModal(true);
  };
  const askDelete = (category) => {
    setDeleteId(category.id);
    setDeleteModal(true);
  };

  const filtered = useMemo(() => {
    const q = searchValue.trim().toLowerCase();
    if (!q) return null;
    return flattenWithPath(categories).filter(({ node }) => node.name.toLowerCase().includes(q));
  }, [categories, searchValue]);

  const isEmpty = !loading && categories.length === 0;

  return (
    <div className="w-full p-6 text-start">
      <PageHeader
        title="Categories"
        subtitle="Drag to reorder, drag right to nest — up to 3 levels."
        actions={
          <>
            <Input
              icon={MagnifyingGlass}
              placeholder="Search categories…"
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              containerClassName="w-full sm:w-64"
            />
            <Button variant="outline" icon={DownloadSimple} onClick={() => setImportModal(true)}>
              Import
            </Button>
            {canManage.create && (
              <Button icon={Plus} onClick={() => openCreate(null)}>
                New category
              </Button>
            )}
          </>
        }
      />

      <div className="rounded-2xl border border-line bg-surface p-3 shadow-card">
        {/* hint bar */}
        {!filtered && !isEmpty && (
          <div className="mb-2 flex items-center gap-2 px-2 py-1.5 text-xs text-muted">
            <DotsSixVertical size={14} />
            <span>Drag the handle to reorder.</span>
            <ArrowsOutCardinal size={14} className="ml-2" />
            <span>Drag horizontally to change nesting.</span>
          </div>
        )}

        {/* loading */}
        {loading && (
          <div className="space-y-2 p-1">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 rounded-xl border border-line px-3 py-2.5" style={{ marginLeft: (i % 3) * 24 }}>
                <Skeleton className="h-7 w-7 rounded-lg" />
                <Skeleton className="h-8 w-8 rounded-lg" />
                <Skeleton className="h-3.5 w-40" />
              </div>
            ))}
          </div>
        )}

        {/* empty */}
        {isEmpty && (
          <EmptyState
            icon={Tag}
            title="No categories yet"
            description="Create your first category to start organizing products."
            action={canManage.create ? <Button icon={Plus} onClick={() => openCreate(null)}>New category</Button> : null}
          />
        )}

        {/* search results (read-only, no DnD) */}
        {!loading && filtered && (
          filtered.length === 0 ? (
            <EmptyState icon={MagnifyingGlass} title="No matches" description="Try a different search term." />
          ) : (
            <div>
              {filtered.map(({ node, path }) => (
                <CategoryRow
                  key={node.id}
                  value={node}
                  depth={0}
                  handleProps={null}
                  pathLabel={path.length ? path.join(" / ") : node.slug}
                  canManage={canManage}
                  onEdit={() => openEdit(node)}
                  onDelete={() => askDelete(node)}
                  onAddChild={path.length < MAX_DEPTH - 1 ? () => openCreate(node.id) : undefined}
                />
              ))}
            </div>
          )
        )}

        {/* full tree with drag & drop */}
        {!loading && !filtered && !isEmpty && (
          <CategoryTree
            items={categories}
            canManage={canManage}
            onChange={(tree) => setCategories(tree)}
            onPersist={persistOrder}
            onEdit={openEdit}
            onDelete={askDelete}
            onAddChild={(parentId) => openCreate(parentId)}
          />
        )}
      </div>

      <CategoryModal
        isOpen={categoryModal}
        onClose={() => {
          setCategoryModal(false);
          setCategoryToUpdate(null);
          setPresetParentId(null);
        }}
        categories={categories}
        CategoryToUpdate={categoryToUpdate}
        presetParentId={presetParentId}
        setDone={setDone}
        done={done}
        setCategoryToUpdate={setCategoryToUpdate}
      />
      <ImportModal
        isOpen={importModal}
        onClose={() => setImportModal(false)}
        categories={categories}
        setDone={setDone}
        done={done}
      />
      <DeleteModal
        setDeleteId={setDeleteId}
        open={deleteModal}
        setOpen={setDeleteModal}
        DeleteId={deleteId}
        Model="categories"
        setDone={setDone}
        setLoding={setLoading}
        done={done}
      />
    </div>
  );
}
